/**
 * VoiceAIRunTimeline - Improved timeline view for Voice AI / BPO runs
 *
 * Shows a clear phase separation between Voice AI calls and BPO handoff,
 * with timeline events, agent attribution, and collected data.
 */
import {
  CheckCircle2,
  Clock,
  MessageSquare,
  Phone,
  PhoneOff,
  Send,
  User,
  XCircle,
} from 'lucide-react';
import React, { useMemo } from 'react';

import {
  BPOAction,
  EnhancedBlandWorkflowStatus,
  VoiceAIAgentRunSchema,
} from '@/types/enhanced-bland-call';

import { formatDate, formatDuration } from './utils';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type TimelineEventType =
  | 'voice_ai_call'
  | 'voice_ai_skip'
  | 'bpo_requested'
  | 'bpo_call_started'
  | 'bpo_completed'
  | 'bpo_failed';

interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  timestamp: string | null;
  title: string;
  description?: string;
  details?: Record<string, string | null>;
  isSuccess?: boolean;
  isFailed?: boolean;
  agentName?: string;
  phoneNumber?: string;
  durationSeconds?: number;
}

interface CollectedData {
  label: string;
  value: string;
}

interface VoiceAIRunTimelineProps {
  run: VoiceAIAgentRunSchema;
  /** Show compact view (less spacing, smaller text) */
  compact?: boolean;
  /** Show the collected data section */
  showCollectedData?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ─────────────────────────────────────────────────────────────────────────────

const formatTimeOnly = (dateString: string | null): string => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '—';
  }
};

const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return 'Unknown number';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const getActionTimestamp = (action?: BPOAction): string | null => {
  if (!action) return null;
  return (
    action.received_at ||
    action.requested_at ||
    action.sla_due_at ||
    action.expired_at ||
    action.cancelled_at ||
    null
  );
};

const getMetadata = (action?: BPOAction): Record<string, unknown> => {
  if (!action || !action.metadata || typeof action.metadata !== 'object') {
    return {};
  }
  return action.metadata as Record<string, unknown>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Timeline Event Extraction
// ─────────────────────────────────────────────────────────────────────────────

const extractTimelineEvents = (run: VoiceAIAgentRunSchema): TimelineEvent[] => {
  const events: TimelineEvent[] = [];
  const bpoActions = run.bpo_actions || [];

  // 1. Voice AI call events
  if (run.call_results && run.call_results.length > 0) {
    run.call_results.forEach((call, idx) => {
      const isSuccess =
        call.evaluation?.success_status === 'success' ||
        call.evaluation?.success_status === 'full_success';
      const isPartial = call.evaluation?.success_status === 'partial_success';
      const isFailed =
        call.evaluation?.success_status === 'failure' ||
        call.evaluation?.success_status === 'no_success';

      events.push({
        id: `voice-call-${idx}`,
        type: 'voice_ai_call',
        timestamp: call.started_at || call.created_at || null,
        title: isSuccess
          ? 'Voice AI call completed'
          : isPartial
            ? 'Voice AI call (partial success)'
            : isFailed
              ? 'Voice AI call failed'
              : 'Voice AI call',
        description: call.summary || call.evaluation?.call_summary || undefined,
        phoneNumber: call.phone_number || undefined,
        durationSeconds: call.duration_seconds ?? call.duration ?? undefined,
        isSuccess,
        isFailed,
      });
    });
  }

  // 2. BPO action events
  // Group actions by dialpad_call_id to show as a single call lifecycle
  const bpoCallGroups = new Map<string, BPOAction[]>();
  const requestedAction = bpoActions.find((a) => a.type === 'requested');

  bpoActions.forEach((action, idx) => {
    if (action.type !== 'requested') {
      const meta = getMetadata(action);
      const callId =
        action.ringcentral_call_id ||
        action.dialpad_call_id ||
        (meta.ringcentral_call_id as string) ||
        (meta.dialpad_call_id as string) ||
        (meta.call_id as string) ||
        `bpo-${idx}`;
      const existing = bpoCallGroups.get(callId) || [];
      existing.push(action);
      bpoCallGroups.set(callId, existing);
    }
  });

  // Add BPO requested event
  if (requestedAction) {
    const meta = getMetadata(requestedAction);
    const slaMinutes = requestedAction.sla_minutes ?? 60;
    events.push({
      id: 'bpo-requested',
      type: 'bpo_requested',
      timestamp: requestedAction.requested_at ?? null,
      title: 'Sent to BPO team',
      description: `Channel: #bpo-claims • SLA: ${slaMinutes} min`,
      details: {
        'Initiated by': (meta.initiated_by_user_email as string) || null,
        'Phone to call': formatPhoneNumber(meta.phone_number as string),
      },
    });
  }

  // Add BPO call events
  bpoCallGroups.forEach((actions, callId) => {
    const chronological = [...actions].sort(
      (a, b) =>
        new Date(getActionTimestamp(a) || 0).getTime() -
        new Date(getActionTimestamp(b) || 0).getTime(),
    );

    const started = chronological.find((a) => a.type === 'call_started');
    const terminal = chronological.filter((a) =>
      ['completed', 'failed', 'cancelled', 'expired'].includes(a.type),
    );
    const latestTerminal = terminal[terminal.length - 1];

    // Find human evaluation (preferred) or latest evaluation
    const humanEval = [...terminal].reverse().find((a) => {
      const meta = getMetadata(a);
      return String(meta.generated_by || '').toLowerCase() === 'human';
    });
    const evalAction = humanEval || latestTerminal;
    const evalMeta = getMetadata(evalAction);

    if (started) {
      const startMeta = getMetadata(started);
      events.push({
        id: `bpo-started-${callId}`,
        type: 'bpo_call_started',
        timestamp: getActionTimestamp(started),
        title: 'BPO agent calling',
        phoneNumber: (startMeta.to_number as string) || undefined,
        agentName:
          (startMeta.ringcentral_user_name as string) ||
          (startMeta.dialpad_user_name as string) ||
          (startMeta.from_number as string) ||
          undefined,
      });
    }

    if (evalAction) {
      const isSuccess =
        evalAction.type === 'completed' || evalMeta.call_success === true;
      const isFailed =
        evalAction.type === 'failed' || evalMeta.call_success === false;
      const outcome = (evalMeta.call_outcome as string) || evalAction.type;

      events.push({
        id: `bpo-terminal-${callId}`,
        type: isSuccess ? 'bpo_completed' : 'bpo_failed',
        timestamp: getActionTimestamp(evalAction),
        title: isSuccess ? 'Call completed' : 'Call failed',
        description: formatOutcome(outcome),
        isSuccess,
        isFailed,
        details: extractCollectedDetails(evalMeta),
      });
    }
  });

  // Sort by timestamp
  return events.sort((a, b) => {
    if (!a.timestamp) return -1;
    if (!b.timestamp) return 1;
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });
};

const formatOutcome = (outcome: string): string => {
  const mapping: Record<string, string> = {
    got_claim_info: 'Got claim info',
    claim_opened: 'Claim opened',
    got_adjuster_info: 'Got adjuster info',
    left_voicemail: 'Left voicemail',
    no_answer: 'No answer',
    wrong_number: 'Wrong number',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    expired: 'Expired (SLA)',
  };
  return mapping[outcome] || outcome.replace(/_/g, ' ');
};

const extractCollectedDetails = (
  meta: Record<string, unknown>,
): Record<string, string | null> => {
  const details: Record<string, string | null> = {};

  if (meta.claim_number) details['Claim #'] = String(meta.claim_number);
  if (meta.policy_number) details['Policy #'] = String(meta.policy_number);
  if (meta.adjuster_name) details.Adjuster = String(meta.adjuster_name);

  // Extract adjuster handles
  const handles = meta.adjuster_handles as
    | Array<{ handle_type: string; value: string }>
    | undefined;
  if (handles && Array.isArray(handles)) {
    handles.forEach((h) => {
      if (h.handle_type === 'PHONE') details.Phone = formatPhoneNumber(h.value);
      if (h.handle_type === 'EMAIL') details.Email = h.value;
      if (h.handle_type === 'FAX') details.Fax = formatPhoneNumber(h.value);
    });
  }

  // Fallback to flat fields
  if (!details.Phone && meta.adjuster_phone) {
    details.Phone = formatPhoneNumber(String(meta.adjuster_phone));
  }
  if (!details.Email && meta.adjuster_email) {
    details.Email = String(meta.adjuster_email);
  }

  return details;
};

const extractCollectedData = (run: VoiceAIAgentRunSchema): CollectedData[] => {
  const data: CollectedData[] = [];
  const bpoActions = run.bpo_actions || [];

  // Find the latest successful completion with data
  const completedActions = bpoActions.filter(
    (a) => a.type === 'completed' && getMetadata(a).call_success === true,
  );

  // Prefer human evaluation
  const humanEval = completedActions.find(
    (a) => String(getMetadata(a).generated_by || '').toLowerCase() === 'human',
  );
  const evalAction = humanEval || completedActions[completedActions.length - 1];

  if (evalAction) {
    const meta = getMetadata(evalAction);
    const details = extractCollectedDetails(meta);

    Object.entries(details).forEach(([label, value]) => {
      if (value) {
        data.push({ label, value });
      }
    });
  }

  return data;
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const TimelineEventIcon: React.FC<{
  type: TimelineEventType;
  isSuccess?: boolean;
  isFailed?: boolean;
}> = ({ type, isSuccess, isFailed }) => {
  const baseClass = 'h-4 w-4';

  if (isSuccess) {
    return <CheckCircle2 className={`${baseClass} text-green-600`} />;
  }
  if (isFailed) {
    return <XCircle className={`${baseClass} text-red-500`} />;
  }

  switch (type) {
    case 'voice_ai_call':
      return <Phone className={`${baseClass} text-blue-500`} />;
    case 'voice_ai_skip':
      return <PhoneOff className={`${baseClass} text-zinc-400`} />;
    case 'bpo_requested':
      return <Send className={`${baseClass} text-amber-500`} />;
    case 'bpo_call_started':
      return <Phone className={`${baseClass} text-blue-500`} />;
    case 'bpo_completed':
      return <CheckCircle2 className={`${baseClass} text-green-600`} />;
    case 'bpo_failed':
      return <XCircle className={`${baseClass} text-red-500`} />;
    default:
      return <Clock className={`${baseClass} text-zinc-400`} />;
  }
};

const TimelineEventRow: React.FC<{
  event: TimelineEvent;
  isLast: boolean;
  compact?: boolean;
}> = ({ event, isLast, compact }) => {
  return (
    <div className="relative flex gap-3">
      {/* Timeline connector line */}
      {!isLast && (
        <div className="absolute left-[11px] top-6 bottom-0 w-px bg-zinc-200" />
      )}

      {/* Icon */}
      <div
        className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
          event.isSuccess
            ? 'bg-green-100'
            : event.isFailed
              ? 'bg-red-100'
              : event.type === 'bpo_requested'
                ? 'bg-amber-100'
                : 'bg-zinc-100'
        }`}
      >
        <TimelineEventIcon
          type={event.type}
          isSuccess={event.isSuccess}
          isFailed={event.isFailed}
        />
      </div>

      {/* Content */}
      <div className={`flex-1 min-w-0 ${isLast ? '' : 'pb-4'}`}>
        <div className="flex items-center justify-between gap-2">
          <span
            className={`font-medium ${compact ? 'text-xs' : 'text-sm'} text-zinc-900`}
          >
            {event.title}
          </span>
          <span className="text-xs text-zinc-500 whitespace-nowrap">
            {formatTimeOnly(event.timestamp)}
          </span>
        </div>

        {event.description && (
          <p
            className={`${compact ? 'text-[11px]' : 'text-xs'} text-zinc-600 mt-0.5`}
          >
            {event.description}
          </p>
        )}

        {event.phoneNumber && (
          <p className="text-xs text-zinc-500 mt-0.5">
            → {formatPhoneNumber(event.phoneNumber)}
            {event.durationSeconds && (
              <span className="ml-2">
                ({formatDuration(event.durationSeconds)})
              </span>
            )}
          </p>
        )}

        {event.agentName && (
          <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">
            <User className="h-3 w-3" />
            {event.agentName}
          </p>
        )}
      </div>
    </div>
  );
};

const CollectedDataCard: React.FC<{
  data: CollectedData[];
  compact?: boolean;
}> = ({ data, compact }) => {
  if (data.length === 0) return null;

  return (
    <div className="rounded-lg border border-green-200 bg-green-50/50 p-3 mt-3">
      <div
        className={`font-medium ${compact ? 'text-xs' : 'text-sm'} text-green-800 mb-2`}
      >
        Data Collected
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {data.map((item) => (
          <div key={item.label} className="flex gap-2 text-xs">
            <span className="text-zinc-500 min-w-[60px]">{item.label}:</span>
            <span className="text-zinc-800 font-medium truncate">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export const VoiceAIRunTimeline: React.FC<VoiceAIRunTimelineProps> = ({
  run,
  compact = false,
  showCollectedData = true,
}) => {
  const timelineEvents = useMemo(() => extractTimelineEvents(run), [run]);
  const collectedData = useMemo(() => extractCollectedData(run), [run]);

  const voiceAICallCount = run.call_results?.length ?? 0;
  const hasBpoActions = (run.bpo_actions?.length ?? 0) > 0;
  const hasBpoCalls = timelineEvents.some(
    (e) => e.type === 'bpo_call_started' || e.type === 'bpo_completed',
  );

  // Determine Voice AI phase status
  const voiceAIPhaseStatus = useMemo(() => {
    if (voiceAICallCount === 0) {
      // Check if we have BPO - means Voice AI was skipped/unavailable
      if (hasBpoActions) {
        return {
          label: 'Skipped (sent to BPO)',
          icon: '⊘',
          color: 'text-zinc-500',
        };
      }
      return { label: 'No calls attempted', icon: '○', color: 'text-zinc-400' };
    }

    const successfulCalls = (run.call_results || []).filter(
      (c) =>
        c.evaluation?.success_status === 'success' ||
        c.evaluation?.success_status === 'full_success',
    );

    if (successfulCalls.length > 0) {
      return { label: 'Completed', icon: '✓', color: 'text-green-600' };
    }

    return {
      label: `${voiceAICallCount}/${run.max_calls_allowed} calls`,
      icon: '○',
      color: 'text-zinc-500',
    };
  }, [
    voiceAICallCount,
    run.call_results,
    run.max_calls_allowed,
    hasBpoActions,
  ]);

  // Overall status
  const runStatus = run.status;
  const overallStatus = useMemo(() => {
    const isCompleted = runStatus === EnhancedBlandWorkflowStatus.COMPLETED;
    const isFailed =
      runStatus === EnhancedBlandWorkflowStatus.FAILED ||
      runStatus === EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE;
    const isCancelled = runStatus === EnhancedBlandWorkflowStatus.CANCELLED;
    const isInProgress =
      runStatus === EnhancedBlandWorkflowStatus.CALL_IN_PROGRESS ||
      runStatus === EnhancedBlandWorkflowStatus.THINKING ||
      runStatus === EnhancedBlandWorkflowStatus.STARTED;

    if (isCompleted) {
      const viaBpo = hasBpoCalls;
      return {
        label: viaBpo ? 'COMPLETED via BPO' : 'COMPLETED',
        color: 'text-green-700 bg-green-50 border-green-200',
        icon: '✓',
      };
    }
    if (isFailed) {
      return {
        label: 'FAILED',
        color: 'text-red-700 bg-red-50 border-red-200',
        icon: '✗',
      };
    }
    if (isCancelled) {
      return {
        label: 'CANCELLED',
        color: 'text-zinc-700 bg-zinc-50 border-zinc-200',
        icon: '—',
      };
    }
    if (isInProgress) {
      return {
        label: 'IN PROGRESS',
        color: 'text-blue-700 bg-blue-50 border-blue-200',
        icon: '⋯',
      };
    }
    return {
      label: runStatus.replace(/_/g, ' ').toUpperCase(),
      color: 'text-zinc-700 bg-zinc-50 border-zinc-200',
      icon: '•',
    };
  }, [runStatus, hasBpoCalls]);

  // Voice AI events vs BPO events
  const voiceAIEvents = timelineEvents.filter(
    (e) => e.type === 'voice_ai_call' || e.type === 'voice_ai_skip',
  );
  const bpoEvents = timelineEvents.filter(
    (e) =>
      e.type === 'bpo_requested' ||
      e.type === 'bpo_call_started' ||
      e.type === 'bpo_completed' ||
      e.type === 'bpo_failed',
  );

  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50/50">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-semibold ${overallStatus.color}`}
          >
            <span>{overallStatus.icon}</span>
            {overallStatus.label}
          </span>
        </div>
        <span className="text-xs text-zinc-500">
          {formatDate(run.created_at)}
        </span>
      </div>

      <div className="p-4 space-y-4">
        {/* Voice AI Phase */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
              Voice AI Phase
            </div>
            <div className="flex-1 h-px bg-zinc-100" />
          </div>

          {voiceAIEvents.length > 0 ? (
            <div className="pl-1">
              {voiceAIEvents.map((event, idx) => (
                <TimelineEventRow
                  key={event.id}
                  event={event}
                  isLast={
                    idx === voiceAIEvents.length - 1 && bpoEvents.length === 0
                  }
                  compact={compact}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-zinc-500 pl-1">
              <span>{voiceAIPhaseStatus.icon}</span>
              <span className={voiceAIPhaseStatus.color}>
                {voiceAIPhaseStatus.label}
              </span>
            </div>
          )}
        </div>

        {/* BPO Phase (if any) */}
        {bpoEvents.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                BPO Handoff
              </div>
              <div className="flex-1 h-px bg-zinc-100" />
            </div>

            <div className="pl-1">
              {bpoEvents.map((event, idx) => (
                <TimelineEventRow
                  key={event.id}
                  event={event}
                  isLast={idx === bpoEvents.length - 1}
                  compact={compact}
                />
              ))}
            </div>
          </div>
        )}

        {/* Collected Data */}
        {showCollectedData && collectedData.length > 0 && (
          <CollectedDataCard data={collectedData} compact={compact} />
        )}

        {/* Summary */}
        {run.summary && (
          <div className="rounded-lg bg-zinc-50 p-3 text-xs text-zinc-700">
            <MessageSquare className="h-3 w-3 inline-block mr-1.5 text-zinc-400" />
            {run.summary}
          </div>
        )}

        {/* Error message */}
        {run.error_message && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-700">
            <XCircle className="h-3 w-3 inline-block mr-1.5" />
            {run.error_message}
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceAIRunTimeline;
